/**
 * Agent List Page - Table view of all agents (restyled for Contact Tel theme).
 */
import { useEffect, useState, useCallback, useMemo, type ChangeEvent, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { alpha } from '@mui/material/styles';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Analytics,
  Cloud,
  Computer,
  Clear,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RequirePermission } from '../contexts/PermissionContext';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import type { Agent, AgentStats } from '../types';
import usePageTitle from '../hooks/usePageTitle';

const agentTypeConfig: Record<string, { color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary'; icon: React.ReactNode }> = {
  cloud: { color: 'primary', icon: <Cloud sx={{ fontSize: 16 }} /> },
  local: { color: 'secondary', icon: <Computer sx={{ fontSize: 16 }} /> },
};

export default function AgentListPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:agents.title'));
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalAgents, setTotalAgents] = useState(0);
  const [statsAgent, setStatsAgent] = useState<Agent | null>(null);
  const [agentStats, setAgentStats] = useState<AgentStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { formatNumber, formatPercent, formatDuration, formatCurrency } = useNumberFormat();
  const { confirm, DialogComponent } = useConfirmDialog();

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'direction', 'search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });

  const statusFilterValue = filters.status ?? '';
  const directionFilterValue = filters.direction ?? '';
  const searchFilterValue = filters.search ?? '';
  const orderingParam = ordering || '-created_at';

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('common:status.active') },
      { value: 'inactive', label: t('common:status.inactive') },
    ],
    [t],
  );

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }
      if (directionFilterValue) {
        params.direction = directionFilterValue;
      }

      const response = await api.get('/api/agents/', {
        params,
      });

      const data = response.data.results ?? response.data;
      setAgents(Array.isArray(data) ? data : []);
      setTotalAgents(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading agents:', err);
      setError(error?.response?.data?.detail || t('pages:agents.errors.loadFailed'));
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [directionFilterValue, orderingParam, page, rowsPerPage, searchFilterValue, statusFilterValue, t]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleDelete = async (agent: Agent) => {
    const confirmed = await confirm({
      title: t('pages:agents.deleteConfirm.title'),
      message: t('pages:agents.deleteConfirm.message', { name: agent.name }),
      type: 'warning',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
      confirmText: t('common:actions.delete'),
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/agents/${agent.id}/`);
      fetchAgents();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error?.response?.data?.error || t('pages:agents.errors.deleteFailed'));
    }
  };

  const handleViewStats = async (agent: Agent) => {
    setStatsAgent(agent);
    setLoadingStats(true);
    try {
      const response = await api.get(`/api/agents/${agent.id}/stats/`);
      setAgentStats(response.data);
    } catch (err) {
      console.error('Error loading agent stats:', err);
      setAgentStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const getAgentTypeLabel = (agentType: string) => {
    switch (agentType) {
      case 'cloud':
        return 'Cloud';
      case 'local':
        return 'Local';
      default:
        return agentType;
    }
  };

  const statusChipStyles = (active: boolean) => ({
    color: active ? theme.palette.success.main : theme.palette.text.secondary,
    bgcolor: active ? alpha(theme.palette.success.main, 0.25) : alpha(theme.palette.text.secondary, 0.15),
  });

  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
    backgroundImage: 'none',
  };

  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };

  const headerActions = (
    <>
      <ListActionButton
        variant="outlined"
        startIcon={<Refresh />}
        onClick={fetchAgents}
        disabled={loading}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="agents_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/agents/new"
        >
          {t('pages:agents.new')}
          </ListActionButton>
      </RequirePermission>
    </>
  );

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:agents.filters.searchPlaceholder')}
        value={searchFilterValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setParams({ search: value || undefined }, { resetPage: true });
        }}
        sx={{ ...filterInputSx, minWidth: 220, flex: '1 1 220px' }}
      />
      <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
        <InputLabel id="agents-status-filter-label">{t('pages:agents.filters.statusLabel')}</InputLabel>
        <Select
          labelId="agents-status-filter-label"
          value={statusFilterValue}
          label={t('pages:agents.filters.statusLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ status: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
        <InputLabel id="agents-direction-filter-label">{t('pages:agents.filters.directionLabel')}</InputLabel>
        <Select
          labelId="agents-direction-filter-label"
          value={directionFilterValue}
          label={t('pages:agents.filters.directionLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ direction: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value=""><em>{t('common:common.all')}</em></MenuItem>
          <MenuItem value="outbound">{t('pages:agents.directions.outbound')}</MenuItem>
          <MenuItem value="inbound">{t('pages:agents.directions.inbound')}</MenuItem>
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Clear />}
        disabled={!searchFilterValue && !statusFilterValue && !directionFilterValue}
        onClick={() => {
          if (!searchFilterValue && !statusFilterValue && !directionFilterValue) return;
          setParams({ search: null, status: null, direction: null }, { resetPage: true });
        }}
        sx={{
          ...flatButtonSx,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const table = isMobile ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
      {loading ? (
        [...Array(3)].map((_, index) => (
          <Card
            key={index}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 25px 45px rgba(0,0,0,0.6)'
                  : '0 15px 35px rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Skeleton height={28} width="60%" />
              <Skeleton height={18} sx={{ mt: 1, width: '40%' }} />
              <Skeleton height={32} sx={{ mt: 2 }} />
            </Box>
          </Card>
        ))
      ) : agents.length === 0 ? (
        <Card
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: 'none',
          }}
        >
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <PsychologyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('pages:agents.noAgents')}
            </Typography>
            <RequirePermission permission="agents_create">
              <Button
                variant="outlined"
                startIcon={<Add />}
                component={RouterLink}
                to="/agents/new"
                sx={{ ...flatButtonSx }}
              >
                {t('pages:agents.createFirst')}
              </Button>
            </RequirePermission>
          </Box>
        </Card>
      ) : (
        agents.map((agent) => {
          const typeConfig = agentTypeConfig[agent.agentType] || { color: 'default', icon: null };
          return (
            <Card
              key={agent.id}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 25px 45px rgba(0,0,0,0.6)'
                    : '0 15px 35px rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ px: 2, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
                  {agent.name}
                </Typography>
                {agent.description && (
                  <Typography variant="caption" color="text.secondary">
                    {agent.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    icon={typeConfig.icon as ReactElement}
                    label={getAgentTypeLabel(agent.agentType)}
                    color={typeConfig.color}
                    size="small"
                    variant="outlined"
                  />
                  <Chip label={agent.aiAgentProviderName || '—'} size="small" variant="outlined" />
                  <Chip
                    label={t(`pages:agents.directions.${agent.direction}`)}
                    size="small"
                    color={agent.direction === 'inbound' ? 'info' : 'success'}
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                }}
              >
                <Chip
                  label={agent.isActive ? t('common:status.active') : t('common:status.inactive')}
                  size="small"
                  sx={statusChipStyles(agent.isActive ?? false)}
                />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <RequirePermission permission="agents_update">
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/agents/${agent.id}`}
                      sx={{ ...flatButtonSx }}
                    >
                      {t('common:actions.edit')}
                    </Button>
                  </RequirePermission>
                  <RequirePermission permission="agents_delete">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(agent)}
                      sx={{ ...flatButtonSx }}
                    >
                      {t('common:actions.delete')}
                    </Button>
                  </RequirePermission>
                </Box>
              </Box>
            </Card>
          );
        })
      )}
    </Box>
  ) : (
    <Table>
      <TableHead>
        <TableRow sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}>
          <TableCell>{columnTitle(t('pages:agents.name'))}</TableCell>
          <TableCell>{columnTitle(t('pages:agents.type'))}</TableCell>
          <TableCell>{columnTitle(t('pages:agents.direction'))}</TableCell>
          <TableCell>{columnTitle(t('pages:agents.ai_agent_provider'))}</TableCell>
          <TableCell align="center">{columnTitle(t('pages:agents.campaigns'))}</TableCell>
          <TableCell align="center">{columnTitle(t('pages:agents.calls'))}</TableCell>
          <TableCell align="center">{columnTitle(t('common:actions.actions'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          [...Array(5)].map((_, i) => (
            <TableRow key={i}>
              {[...Array(7)].map((_, j) => (
                <TableCell key={j}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : agents.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
              <PsychologyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('pages:agents.noAgents')}
              </Typography>
              <RequirePermission permission="agents_create">
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  component={RouterLink}
                  to="/agents/new"
                  sx={{ ...flatButtonSx }}
                >
                  {t('pages:agents.createFirst')}
                </Button>
              </RequirePermission>
            </TableCell>
          </TableRow>
        ) : (
          agents.map((agent) => {
            const typeConfig = agentTypeConfig[agent.agentType] || { color: 'default', icon: null };

            return (
              <TableRow key={agent.id} hover>
                <LinkTableCell to={`/agents/${agent.id}`}>
                  <Box>
                    <Typography
                      fontWeight={600}
                      sx={{
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                      }}
                    >
                      {agent.name}
                    </Typography>
                    {agent.description && (
                      <Typography variant="caption" color="text.secondary">
                        {agent.description.substring(0, 60)}
                        {agent.description.length > 60 ? '...' : ''}
                      </Typography>
                    )}
                  </Box>
                </LinkTableCell>
                <LinkTableCell to={`/agents/${agent.id}`}>
                  <Chip
                    icon={typeConfig.icon as ReactElement}
                    label={getAgentTypeLabel(agent.agentType)}
                    color={typeConfig.color}
                    size="small"
                    variant="outlined"
                  />
                </LinkTableCell>
                <LinkTableCell to={`/agents/${agent.id}`}>
                  <Chip
                    label={t(`pages:agents.directions.${agent.direction}`)}
                    size="small"
                    color={agent.direction === 'inbound' ? 'info' : 'success'}
                    variant="outlined"
                  />
                </LinkTableCell>
                <TableCell>
                  {agent.aiAgentProviderName || '-'}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{formatNumber(agent.campaignsCount)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {formatNumber(agent.callsCount ?? 0)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title={t('pages:agents.viewStats')}>
                      <IconButton size="small" onClick={() => handleViewStats(agent)}>
                        <Analytics fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <RequirePermission permission="agents_update">
                      <Tooltip title={t('common:actions.edit')}>
                        <IconButton size="small" component={RouterLink} to={`/agents/${agent.id}`}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </RequirePermission>
                    <RequirePermission permission="agents_delete">
                      <Tooltip title={t('common:actions.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(agent)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </RequirePermission>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  const tableFooter =
    totalAgents > 0 ? (
      <LinkPagination
        component="div"
        count={totalAgents}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={t('common:pagination.rowsPerPage')}
        getItemAriaLabel={(type) => {
          if (type === 'first') return t('common:pagination.firstPage');
          if (type === 'last') return t('common:pagination.lastPage');
          if (type === 'next') return t('common:pagination.nextPage');
          return t('common:pagination.previousPage');
        }}
        getPageLink={getPageLink}
      />
    ) : undefined;

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <PsychologyIcon sx={{ fontSize: 32, color: 'primary.main' }} />, 
          title: t('pages:agents.title'),
          subtitle: t('pages:agents.subtitle'),
          actions: headerActions,
        }}
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
        tableContainerSx={isMobile ? { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } : undefined}
        errorContent={
          error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'error.main', borderRadius: 1 }}>
              <Typography color="error.contrastText">{error}</Typography>
            </Box>
          )
        }
      />

      {statsAgent && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
          onClick={() => setStatsAgent(null)}
        >
          <Card sx={{ p: 3, minWidth: 400, maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <Typography variant="h6" gutterBottom>
              {t('pages:agents.statsTitle', { name: statsAgent.name })}
            </Typography>
            {loadingStats ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Skeleton variant="rectangular" width="100%" height={150} />
              </Box>
            ) : agentStats ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('pages:agents.stats.totalCalls')}
                  </Typography>
                  <Typography variant="h5">{formatNumber(agentStats.totalCalls)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('pages:agents.stats.successRate')}
                  </Typography>
                  <Typography variant="h5">{formatPercent(agentStats.successRate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('pages:agents.stats.avgDuration')}
                  </Typography>
                  <Typography variant="h5">{formatDuration(agentStats.avgDuration, true)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('pages:agents.stats.totalCost')}
                  </Typography>
                  <Typography variant="h5">{formatCurrency(agentStats.totalCost, 'USD')}</Typography>
                </Box>
              </Box>
            ) : (
              <Typography color="text.secondary">{t('pages:agents.stats.noData')}</Typography>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button sx={{ ...flatButtonSx }} onClick={() => setStatsAgent(null)}>
                {t('common:actions.close')}
              </Button>
            </Box>
          </Card>
        </Box>
      )}

      {DialogComponent}
    </Box>
  );
}
